import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  LinearProgress,
  Alert,
  Stack,
  Tooltip,
  Divider,
  Avatar,
  InputAdornment,
  TableSortLabel,
  Collapse,
  CardActions,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  QrCode as BarcodeIcon,
  Science as ScienceIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Inbox as InboxIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon,
  MedicalServices as MedicalIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import api from '../../../../../services/resultEntryService';

export const SampleCollection = () => {
  const [pendingBookings, setPendingBookings] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [bookingType, setBookingType] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [openBookingModal, setOpenBookingModal] = useState(false);
  const [openCollectionModal, setOpenCollectionModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTests, setSelectedTests] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Get user info from localStorage
  const [userInfo, setUserInfo] = useState(null);

  // Load user info from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserInfo(parsedUser);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
  }, []);

  // Status configuration (removed receiving statuses)
  const statusConfig = {
    pending: { color: 'default', label: 'PENDING', icon: <ScheduleIcon fontSize="small" /> },
    collection_in_progress: { color: 'warning', label: 'IN PROGRESS', icon: <PlayArrowIcon fontSize="small" /> },
    partially_collected: { color: 'info', label: 'PARTIAL', icon: <RefreshIcon fontSize="small" /> },
    fully_collected: { color: 'success', label: 'COLLECTED', icon: <CheckIcon fontSize="small" /> },
    received_in_lab: { color: 'primary', label: 'IN LAB', icon: <ScienceIcon fontSize="small" /> }
  };

  // Fetch pending bookings
  const fetchPendingBookings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sample-collection/pending-bookings', {
        params: {
          type: bookingType,
          search: searchText
        }
      });
      setPendingBookings(response.data.data || []);
    } catch (error) {
      showSnackbar('Failed to fetch pending bookings', 'error');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing collections (only pending/collection in progress)
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sample-collection/pending', {
        params: {
          type: bookingType,
          search: searchText
        }
      });
      setCollections(response.data.data || []);
    } catch (error) {
      showSnackbar('Failed to fetch collections', 'error');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchPendingBookings();
    } else {
      fetchCollections();
    }
  }, [bookingType, searchText, tabValue]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar({ ...snackbar, open: false });
    }, 3000);
  };

  // Initialize collection from booking
  const handleInitializeFromBooking = async (booking) => {
    setSelectedBooking(booking);
    setOpenBookingModal(true);
  };

  // Initialize collection - automatically get collector info from localStorage
  const handleInitializeCollection = async () => {
    if (!userInfo) {
      showSnackbar('User information not found. Please login again.', 'error');
      return;
    }

    if (!selectedBooking) {
      showSnackbar('No booking selected', 'error');
      return;
    }

    try {
      const response = await api.post('/sample-collection/initialize', {
        bill_no: selectedBooking.bill_no,
        collector_id: userInfo._id, // Use user ID from localStorage
        collector_name: userInfo.name, // Use user name from localStorage
        source: selectedBooking.source
      });

      showSnackbar('Collection initialized successfully');
      setSelectedCollection(response.data.data);
      setSelectedBooking(null);
      setOpenBookingModal(false);
      setActiveStep(1);
      setOpenCollectionModal(true);
      fetchPendingBookings();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to initialize collection', 'error');
    }
  };

  // Collect samples (updated to close modal after collection)
  const handleCollectSamples = async (event) => {
    event.preventDefault();

    if (!selectedCollection || selectedTests.length === 0) {
      showSnackbar('Please select tests to collect', 'error');
      return;
    }

    if (!userInfo) {
      showSnackbar('User information not found. Please login again.', 'error');
      return;
    }

    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData);

    try {
      const testCollections = selectedTests.map(testId => ({
        test_id: testId,
        actual_collection_time: new Date().toISOString(),
        collection_notes: values.collection_notes,
        sample_type: values.sample_type,
        sample_container: values.sample_container,
        collection_site: values.collection_site
      }));

      await api.post('/sample-collection/collect', {
        collection_id: selectedCollection._id,
        test_collections: testCollections,
        collected_by: userInfo._id, // Add user ID
        collected_by_name: userInfo.name // Add user name
      });

      showSnackbar('Samples collected successfully');

      // Close modal and reset states
      setActiveStep(0);
      setSelectedCollection(null);
      setSelectedTests([]);
      setOpenCollectionModal(false);

      // Refresh collections list
      fetchCollections();

    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to collect samples', 'error');
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get source color
  const getSourceColor = (source) => {
    switch (source) {
      case 'OPD': return 'primary';
      case 'IPD': return 'success';
      case 'INVESTIGATION': return 'warning';
      default: return 'default';
    }
  };

  // Get source icon
  const getSourceIcon = (source) => {
    switch (source) {
      case 'OPD': return <MedicalIcon fontSize="small" />;
      case 'IPD': return <HospitalIcon fontSize="small" />;
      case 'INVESTIGATION': return <AssessmentIcon fontSize="small" />;
      default: return <ReceiptIcon fontSize="small" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Get status chip for collections
  const StatusChip = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        color={config.color}
        variant="filled"
      />
    );
  };

  // Updated steps - only collection related
  const steps = ['Initialize Collection', 'Collect Samples'];

  // Calculate paginated data
  const paginatedData = tabValue === 0
    ? pendingBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : collections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
    setExpandedRow(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ mb: 2 }}
        >
          {snackbar.message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <ScienceIcon color="primary" />
              <Typography variant="h6">Sample Collection Dashboard</Typography>
            </Stack>
          }
          action={
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={bookingType}
                  label="Type"
                  onChange={(e) => setBookingType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="OPD">OPD</MenuItem>
                  <MenuItem value="IPD">IPD</MenuItem>
                  <MenuItem value="INVESTIGATION">Investigation</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search by bill no, patient, UHID..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ width: 300 }}
              />

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => tabValue === 0 ? fetchPendingBookings() : fetchCollections()}
              >
                Refresh
              </Button>
            </Stack>
          }
        />

        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ReceiptIcon fontSize="small" />
                  <Typography>Pending Bookings</Typography>
                  {pendingBookings.length > 0 && (
                    <Chip label={pendingBookings.length} size="small" color="warning" />
                  )}
                </Stack>
              }
            />
            <Tab
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ScienceIcon fontSize="small" />
                  <Typography>Active Collections</Typography>
                  {collections.length > 0 && (
                    <Chip label={collections.length} size="small" color="primary" />
                  )}
                </Stack>
              }
            />
          </Tabs>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {tabValue === 0 ? (
                    <>
                      <TableCell>Bill No</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>UHID</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Doctor</TableCell>
                      <TableCell align="center">Pending Tests</TableCell>
                      <TableCell>Booking Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>Barcode</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>UHID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Pending Tests</TableCell>
                      <TableCell>Collection Time</TableCell>
                      <TableCell>Actions</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {tabValue === 0
                          ? 'No pending bookings found'
                          : 'No active collections found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item) => (
                    <React.Fragment key={item._id}>
                      <TableRow hover>
                        {tabValue === 0 ? (
                          // Pending Bookings Table
                          <>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <ReceiptIcon fontSize="small" color="primary" />
                                <Typography variant="body2" fontWeight="medium">
                                  {item.bill_no}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{item.patient_name}</TableCell>
                            <TableCell>{item.uhid}</TableCell>
                            <TableCell>
                              <Chip
                                icon={getSourceIcon(item.source)}
                                label={item.source}
                                size="small"
                                color={getSourceColor(item.source)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{item.doctor_name}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.pending_tests_count}
                                size="small"
                                color="warning"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(item.date)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Tooltip title="Initialize Collection">
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleInitializeFromBooking(item)}
                                  >
                                    Initialize
                                  </Button>
                                </Tooltip>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(item._id)}
                                  >
                                    {expandedRow === item._id ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </>
                        ) : (
                          // Active Collections Table
                          <>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <BarcodeIcon fontSize="small" />
                                <Typography variant="body2" fontWeight="medium">
                                  {item.barcode_no}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{item.patient?.name}</TableCell>
                            <TableCell>{item.uhid}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.booking_type}
                                size="small"
                                color={getSourceColor(item.booking_type)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <StatusChip status={item.overall_status} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.pending_count}
                                size="small"
                                color="warning"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(item.collection_session?.start_time)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Tooltip title="Continue Collection">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      setSelectedCollection(item);
                                      setActiveStep(1);
                                      setOpenCollectionModal(true);
                                    }}
                                  >
                                    <PlayArrowIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(item._id)}
                                  >
                                    {expandedRow === item._id ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </>
                        )}
                      </TableRow>

                      {/* Expanded Row - Test Details */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expandedRow === item._id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Test Details
                              </Typography>
                              <Grid container spacing={2}>
                                {(tabValue === 0 ? item.service_items : item.test_collections)?.map((test, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Stack spacing={1}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {tabValue === 0 ? test.service_name : test.test_name}
                                          </Typography>
                                          {tabValue === 0 ? (
                                            <>
                                              <Typography variant="caption" color="text.secondary">
                                                Quantity: {test.quantity}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                Rate: ₹{test.rate}
                                              </Typography>
                                            </>
                                          ) : (
                                            <>
                                              <Typography variant="caption" color="text.secondary">
                                                Status: {test.test_status}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                Collection Time: {formatDate(test.collection_info?.actual_collection_time)}
                                              </Typography>
                                            </>
                                          )}
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={tabValue === 0 ? pendingBookings.length : collections.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Booking Modal - Initialize Collection */}
      <Dialog
        open={openBookingModal}
        onClose={() => {
          setOpenBookingModal(false);
          setSelectedBooking(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <AddIcon color="primary" />
            <Typography variant="h6">Initialize Sample Collection</Typography>
          </Stack>
        </DialogTitle>

        {selectedBooking && (
          <>
            <DialogContent dividers>
              {/* Booking Summary */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Bill Number
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedBooking.bill_no}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Source
                        </Typography>
                        <Chip
                          label={selectedBooking.source}
                          size="small"
                          color={getSourceColor(selectedBooking.source)}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Patient
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedBooking.patient_name}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          UHID
                        </Typography>
                        <Typography variant="body2">
                          {selectedBooking.uhid}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Doctor
                        </Typography>
                        <Typography variant="body2">
                          {selectedBooking.doctor_name}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Pending Tests
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {selectedBooking.pending_tests_count} tests
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Collector Information Display (from localStorage) */}
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'info.light' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    Collector Information
                  </Typography>
                  <Stack spacing={2}>
                    {userInfo ? (
                      <>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Collector Name:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {userInfo.name}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Collector ID:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {userInfo._id}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Role:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {userInfo.role}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Organization:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {userInfo.nameOfOrg}
                          </Typography>
                        </Stack>
                      </>
                    ) : (
                      <Alert severity="warning">
                        User information not found. Please login again.
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* Confirmation Message */}
              <Alert severity="info" sx={{ mb: 2 }}>
                Collection will be initialized with your user credentials. You will be recorded as the collector.
              </Alert>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => {
                  setOpenBookingModal(false);
                  setSelectedBooking(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleInitializeCollection}
                disabled={!userInfo}
              >
                Initialize Collection
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Collection Modal - Collect Samples */}
      <Dialog
        open={openCollectionModal}
        onClose={() => {
          setOpenCollectionModal(false);
          setSelectedCollection(null);
          setActiveStep(0);
          setSelectedTests([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent dividers>
          {selectedCollection && activeStep === 1 && (
            <Box component="form" onSubmit={handleCollectSamples}>
              {/* Collection Summary */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Barcode
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCollection.barcode_no}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Patient
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCollection.patient?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        UHID
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCollection.uhid}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Collector
                      </Typography>
                      <Typography variant="body2">
                        {selectedCollection.collection_session?.collector_name || 'Not assigned'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Collection Details */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sample Type</InputLabel>
                    <Select name="sample_type" label="Sample Type" defaultValue="whole_blood">
                      <MenuItem value="whole_blood">Whole Blood</MenuItem>
                      <MenuItem value="serum">Serum</MenuItem>
                      <MenuItem value="plasma">Plasma</MenuItem>
                      <MenuItem value="urine">Urine</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="sample_container"
                    label="Sample Container"
                    fullWidth
                    defaultValue="Lavender"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Collection Site</InputLabel>
                    <Select
                      name="collection_site"
                      label="Collection Site"
                      defaultValue="left_arm"
                    >
                      <MenuItem value="left_arm">Left Arm</MenuItem>
                      <MenuItem value="right_arm">Right Arm</MenuItem>
                      <MenuItem value="left_hand">Left Hand</MenuItem>
                      <MenuItem value="right_hand">Right Hand</MenuItem>
                      <MenuItem value="finger">Finger</MenuItem>
                      <MenuItem value="heel">Heel</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="collection_notes"
                    label="Collection Notes"
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>

              {/* Tests Table */}
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Select Tests to Collect
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTests.length === selectedCollection.test_collections?.length}
                          indeterminate={
                            selectedTests.length > 0 &&
                            selectedTests.length < selectedCollection.test_collections?.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTests(selectedCollection.test_collections?.map(t => t.test_id) || []);
                            } else {
                              setSelectedTests([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Test Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Collection Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCollection.test_collections?.map((test) => (
                      <TableRow key={test.test_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTests.includes(test.test_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTests([...selectedTests, test.test_id]);
                              } else {
                                setSelectedTests(selectedTests.filter(id => id !== test.test_id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{test.test_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={test.test_status?.toUpperCase()}
                            size="small"
                            color={test.test_status === 'collected' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {test.collection_info?.actual_collection_time
                            ? new Date(test.collection_info.actual_collection_time).toLocaleString()
                            : 'Not collected'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <DialogActions sx={{ px: 0 }}>
                <Button onClick={() => {
                  setActiveStep(0);
                  setOpenCollectionModal(false);
                }}>
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={selectedTests.length === 0}
                >
                  Collect Selected Tests ({selectedTests.length})
                </Button>
              </DialogActions>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};